"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html, nothing } from "lit";
import { state, property, query } from "lit/decorators.js";
import { STCComponentMixin } from "../../../common/mixins/stc_component_mixin";
import {
  isDuplicatePageTitle,
  DUPLICATE_PAGE_TITLE_MESSAGE
} from "./microsite_page_manager";
import { titleToSlug } from "./helper_utils";
export class MicrositeEditPageDrawer extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this.page = null;
    this.pages = [];
    this.editorMode = "visual";
    this._opened = false;
  }
  get opened() {
    return this._opened;
  }
  open(page, pages) {
    this.page = page ? { ...page } : null;
    this.pages = pages?.length ? [...pages] : [];
    this._opened = true;
  }
  close() {
    this._opened = false;
    this.page = null;
    this.pages = [];
  }
  get isHtmlEditorMode() {
    return this.editorMode === "html";
  }
  get showPageOrder() {
    return !this.isHtmlEditorMode && this.pages.length > 1;
  }
  get drawerSubHeading() {
    return this.isHtmlEditorMode ? "Update the name of the page." : "Update the name of the page or the order of the pages in the menu.";
  }
  get pageOrderRankListInput() {
    return this.pages.map((p, index) => ({
      title: `${p.title}${p.is_home_page ? " (Home)" : ""}`,
      value: String(p.id),
      order: index
    }));
  }
  handleCloseEnded() {
    this.close();
    this.dispatchEvent(
      new CustomEvent("microsite:edit-drawer-closed", {
        bubbles: true,
        composed: true
      })
    );
  }
  handleSubmitEnded(e) {
    if (!this.page) return;
    const formValue = e.detail?.value || {};
    const title = String(formValue.title ?? "").trim();
    if (!title) return;
    this.dispatchPagesUpdate(title);
  }
  /** Build updated pages from title and rank list order, dispatch, and close. */
  dispatchPagesUpdate(title) {
    if (!this.page) return;
    if (isDuplicatePageTitle(this.pages, title, this.page.id)) {
      const form = this.querySelector("#page-settings-drawer-form");
      form?.validateCustomValidity?.({ title: [DUPLICATE_PAGE_TITLE_MESSAGE] });
      return;
    }
    const slug = titleToSlug(title) || null;
    const updatedAt = (/* @__PURE__ */ new Date()).toISOString();
    let updatedPages;
    if (this.isHtmlEditorMode) {
      updatedPages = this.pages.map(
        (p) => p.id === this.page.id ? { ...p, title, slug, updated_at: updatedAt } : p
      );
    } else {
      const rankedList = this.pageOrderRankListEl?.list?.() ?? [];
      const orderedIds = rankedList.length > 0 ? rankedList.map((item) => Number(item.value)) : this.pages.map((p) => p.id);
      const pageById = new Map(this.pages.map((p) => [p.id, p]));
      const reordered = orderedIds.filter((id) => pageById.has(id)).map((id, index) => {
        const p = pageById.get(id);
        return { ...p, order: index };
      });
      updatedPages = reordered.map(
        (p) => p.id === this.page.id ? { ...p, title, slug, updated_at: updatedAt } : p
      );
    }
    this.dispatchEvent(
      new CustomEvent("microsite:pages-update", {
        bubbles: true,
        composed: true,
        detail: { pages: updatedPages }
      })
    );
    this.close();
  }
  /** Save via button click: submit the form so it validates and fires submit-ended. */
  handleSaveClick() {
    if (!this.page) return;
    const formEl = this.querySelector("#page-settings-drawer-form");
    formEl?.submit?.();
  }
  /** Cancel: discard changes and close (no save). close-ended from hi-drawer will fire microsite:edit-drawer-closed. */
  handleCancelClick() {
    this.close();
  }
  render() {
    const page = this.page;
    if (!page) return nothing;
    return html`
      <hi-drawer
        id="page-settings-drawer"
        heading="Edit Page Settings"
        sub-heading=${this.drawerSubHeading}
        ?opened=${this._opened}
        @close-ended=${this.handleCloseEnded}
      >
        <hi-form
          id="page-settings-drawer-form"
          form="page-settings-drawer-form"
          disable-http
          @submit-ended=${this.handleSubmitEnded}
        >
          <hi-input-group>
            <hi-label>Page Name</hi-label>
            <hi-input
              type="text"
              name="title"
              form="page-settings-drawer-form"
              .value=${page.title}
              placeholder="Enter page name"
              required
            ></hi-input>
          </hi-input-group>
          ${this.showPageOrder ? html`
                <hi-input-group>
                  <hi-label>Page Order</hi-label>
                  <hi-rank-list
                    id="page-order-ranklist"
                    name="page_order"
                    form="page-settings-drawer-form"
                    .input=${this.pageOrderRankListInput}
                  ></hi-rank-list>
                </hi-input-group>
              ` : nothing}
        </hi-form>
        <div class="eab-page-manager__drawer-actions mtl">
          <hi-button
            type="button"
            slot="controls"
            aesthetic="primary"
            @trigger-ended=${this.handleSaveClick}
          >
            <span>Save Settings</span>
          </hi-button>
          <hi-button
            type="button"
            slot="controls"
            aesthetic="ghost"
            @trigger-ended=${this.handleCancelClick}
          >
            <span>Cancel</span>
          </hi-button>
        </div>
      </hi-drawer>
    `;
  }
}
__decorateClass([
  property({ type: Object })
], MicrositeEditPageDrawer.prototype, "page", 2);
__decorateClass([
  property({ type: Array })
], MicrositeEditPageDrawer.prototype, "pages", 2);
__decorateClass([
  property({ type: String, attribute: "editor-mode", reflect: true })
], MicrositeEditPageDrawer.prototype, "editorMode", 2);
__decorateClass([
  state()
], MicrositeEditPageDrawer.prototype, "_opened", 2);
__decorateClass([
  query("#page-order-ranklist")
], MicrositeEditPageDrawer.prototype, "pageOrderRankListEl", 2);
customElements.define("stc-microsite-edit-page-drawer", MicrositeEditPageDrawer);
